import type { StaticEntityType } from "@config";
import Spinner from "@components/ui/spinner";
import Switch from "@components/ui/switch";
import Tooltip from "@components/ui/tooltip";
import { cn } from "@lib/cn";
import { SlidePreview } from "@modules/slides-library/shared";
import {
  IconArrowBackUp,
  IconArrowRight,
  IconSparkles,
  IconThumbDown,
  IconThumbUp,
} from "@tabler/icons-react";
import type { CSSProperties } from "react";
import {
  FILL_FAILED_MESSAGE,
  type ImportRow,
  REGENERATE_FAILED_CODE,
  REGENERATE_FAILED_MESSAGE,
  type RowRating,
} from "../stores/file-to-storydoc-generation.store";

const LAPTOP_WIDTH = 1440;
export const previewScale = (frameWidth: number) =>
  ({
    "--slide-preview-scaled-size": `${((LAPTOP_WIDTH / frameWidth) * 100).toFixed(2)}%`,
    "--slide-preview-scale": (frameWidth / LAPTOP_WIDTH).toFixed(4),
  }) as CSSProperties;
const LARGE_W = 444;
const SMALL_W = 83;

export const STATIC_TOOLTIP =
  "This converts the source slide into a static image and places it on your Storydoc slide. It won't adapt to mobile screens";

const failureCopy = (code: string) =>
  code === REGENERATE_FAILED_CODE
    ? REGENERATE_FAILED_MESSAGE
    : FILL_FAILED_MESSAGE;

const SkeletonTile = ({ size }: { size: "large" | "small" }) => {
  const large = size === "large";
  return (
    <div
      aria-hidden="true"
      className={cn(
        "tw-relative tw-flex tw-shrink-0 tw-animate-pulse tw-items-center tw-justify-center tw-rounded-lg",
        large ? "tw-h-[256px] tw-w-[444px]" : "tw-h-12 tw-w-[83px]",
      )}
    >
      <div className="tw-absolute tw-inset-0 tw-rounded-lg tw-bg-blue-50 tw-opacity-50 tw-shadow-xs tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5" />
      <div
        className={cn(
          "tw-relative tw-flex tw-flex-col tw-opacity-20",
          large ? "tw-w-[233px] tw-gap-2" : "tw-w-[50px] tw-gap-[2px]",
        )}
      >
        {[0, 1, 2, 3].map((line) => (
          <span
            key={line}
            className={cn(
              "tw-block tw-rounded-full tw-bg-blue-500",
              large ? "tw-h-2" : "tw-h-[2px]",
              line === 3 && (large ? "tw-w-[98px]" : "tw-w-[21px]"),
            )}
          />
        ))}
      </div>
    </div>
  );
};

const RetryLink = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="tw-font-medium tw-text-violet-700 tw-underline hover:tw-text-violet-500"
  >
    Retry
  </button>
);

const CONTROL_CLASS =
  "tw-flex tw-h-8 tw-items-center tw-justify-center tw-gap-0.5 tw-bg-white tw-text-xs tw-font-medium tw-text-blue tw-shadow-2xs tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5 tw-transition-colors hover:tw-bg-blue-50 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white [&_svg]:tw-size-[14px] [&_svg]:!tw-fill-none";

const RatingButton = ({
  rating,
  active,
  disabled,
  onClick,
}: {
  rating: RowRating;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) => {
  const Icon = rating === "up" ? IconThumbUp : IconThumbDown;
  return (
    <button
      type="button"
      aria-label={rating === "up" ? "Thumbs up" : "Thumbs down"}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        CONTROL_CLASS,
        "tw-w-8",
        rating === "down" ? "tw-rounded-l-lg" : "tw-rounded-r-lg",
        active && "tw-bg-violet-50 tw-text-violet-700 hover:tw-bg-violet-50",
      )}
    >
      <Icon />
    </button>
  );
};

type VersionRowProps = {
  row: ImportRow;
  requesting: boolean;
  onSelect: (k: number) => void;
  onStatic: (on: boolean) => void;
  onRegenerate: () => void;
  onUndo: () => void;
  onRate: (rating: RowRating) => void;
};

export const VersionRow = ({
  row,
  requesting,
  onSelect,
  onStatic,
  onRegenerate,
  onUndo,
  onRate,
}: VersionRowProps) => {
  const regenerating = requesting || row.regenerating != null;
  const busy = regenerating || row.undoing;
  const hasOptions = row.options != null;
  const failedWithoutOptions =
    !hasOptions && row.failure != null && !row.asStatic;
  const regenerateFailed = hasOptions && row.failure != null && !busy;
  const current = row.asStatic
    ? row.sourceSlide
    : (row.options?.[row.selected]?.slide ?? null);
  const showSkeleton =
    (busy && !row.asStatic) || (current == null && !failedWithoutOptions);
  const settled = hasOptions || row.failure != null || row.asStatic;
  const showActions = busy || !failedWithoutOptions;
  const optionSkeletons =
    busy || (!hasOptions && row.failure == null && !row.asStatic);
  const optionsLocked = row.asStatic || busy;
  const canRegenerate = !busy && (hasOptions || row.failure != null);
  const canRate = !busy && (hasOptions || row.asStatic);
  const failureText = row.failure ? failureCopy(row.failure.code) : null;
  const controlsDimmed = !settled;

  return (
    <div className="tw-flex tw-items-start tw-justify-center tw-gap-8">
      <div className="tw-flex tw-w-[191px] tw-shrink-0 tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-col tw-gap-1.5 tw-overflow-hidden tw-rounded-lg tw-bg-white tw-pb-1.5 tw-shadow-sm tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5">
          {row.thumbnail ? (
            <img
              src={row.thumbnail}
              alt={`Source slide ${row.pageNumber}`}
              className="tw-block tw-h-[108px] tw-w-full tw-border-0 tw-border-b tw-border-solid tw-border-blue/5 tw-object-cover"
            />
          ) : (
            <div className="tw-h-[108px] tw-w-full tw-border-0 tw-border-b tw-border-solid tw-border-blue/5 tw-bg-blue-50" />
          )}
          <div className="tw-flex tw-h-8 tw-items-center tw-justify-between tw-px-4">
            <span className="tw-text-sm tw-font-medium tw-text-blue">
              Source slide
            </span>
            <span className="tw-text-xs tw-text-blue-500">
              Slide {row.pageNumber}
            </span>
          </div>
        </div>
        <Tooltip placement="bottom">
          <Tooltip.Trigger asChild>
            <div className="tw-rounded-full tw-bg-blue-50 tw-py-2 tw-pl-3 tw-pr-2">
              <Switch className="tw-w-full tw-justify-between tw-gap-2">
                <Switch.Label className="tw-text-xs tw-font-normal tw-text-blue">
                  Use as a static image
                </Switch.Label>
                <Switch.Toggle
                  checked={row.asStatic}
                  disabled={row.sourceSlide == null}
                  onCheckedChange={onStatic}
                  aria-label="Use as a static image"
                />
              </Switch>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <Tooltip.Text className="tw-max-w-[260px]">
              {STATIC_TOOLTIP}
            </Tooltip.Text>
          </Tooltip.Content>
        </Tooltip>
      </div>

      <div className="tw-flex tw-h-[152px] tw-shrink-0 tw-items-center">
        <span
          aria-hidden="true"
          className="tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-100 tw-text-blue-500"
        >
          <IconArrowRight className="tw-size-[14px]" />
        </span>
      </div>

      <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-center tw-gap-4">
        <div className="tw-flex tw-items-start tw-gap-4">
          <div className="tw-shrink-0" style={{ width: LARGE_W }}>
            {failedWithoutOptions && !busy ? (
              <div
                role="alert"
                className="tw-flex tw-h-[256px] tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-bg-blue-50 tw-text-sm tw-text-blue-700"
              >
                <span>{failureText}</span>
                <RetryLink onClick={onRegenerate} />
              </div>
            ) : showSkeleton ? (
              <SkeletonTile size="large" />
            ) : (
              <SlidePreview
                slide={current as StaticEntityType}
                isIntersecting
                shouldRender
                className="tw-shadow-xs tw-outline-blue/5"
                style={previewScale(LARGE_W)}
              />
            )}
          </div>

          <div
            className={cn(
              "tw-flex tw-shrink-0 tw-flex-col tw-gap-4",
              optionsLocked && "tw-pointer-events-none",
              row.asStatic && !optionSkeletons && "tw-opacity-30",
            )}
            style={{ width: SMALL_W }}
            aria-busy={busy}
          >
            {optionSkeletons
              ? [0, 1, 2].map((i) => <SkeletonTile key={i} size="small" />)
              : row.options?.map((o, i) => (
                  <button
                    type="button"
                    key={o.k}
                    title={o.title}
                    aria-pressed={row.selected === i}
                    disabled={optionsLocked}
                    onClick={() => onSelect(o.k)}
                    className={cn(
                      "tw-relative tw-block tw-w-full tw-overflow-hidden tw-rounded-lg tw-text-left tw-shadow-xs tw-outline tw-transition-colors after:tw-pointer-events-none after:tw-absolute after:tw-inset-0 after:tw-rounded-lg after:tw-content-['']",
                      row.selected === i
                        ? "tw-outline-0 after:tw-outline after:tw-outline-2 after:-tw-outline-offset-2 after:tw-outline-violet-700"
                        : "tw-outline-1 tw-outline-offset-0 tw-outline-blue/5 hover:tw-outline-violet-300",
                    )}
                  >
                    <SlidePreview
                      slide={o.slide as StaticEntityType}
                      isIntersecting
                      shouldRender
                      className="tw-rounded-none tw-shadow-none !tw-outline-0"
                      style={previewScale(SMALL_W)}
                    />
                  </button>
                ))}
          </div>
        </div>

        {regenerateFailed && (
          <p role="alert" className="tw-m-0 tw-text-xs tw-text-blue-700">
            {failureText} <RetryLink onClick={onRegenerate} />
          </p>
        )}

        {showActions && (
          <div
            className={cn(
              "tw-flex tw-items-center tw-justify-center tw-gap-2",
              controlsDimmed && "tw-opacity-30",
            )}
            style={{ width: LARGE_W }}
          >
            <button
              type="button"
              className={cn(CONTROL_CLASS, "tw-rounded-lg tw-px-3")}
              disabled={!canRegenerate}
              aria-busy={regenerating}
              onClick={onRegenerate}
            >
              {regenerating ? (
                <Spinner className="tw-size-3 tw-border" />
              ) : (
                <IconSparkles />
              )}
              <span className="tw-px-0.5">
                {regenerating ? "Regenerating…" : "Regenerate"}
              </span>
            </button>
            {row.undo && (
              <button
                type="button"
                className={cn(CONTROL_CLASS, "tw-rounded-lg tw-px-3")}
                disabled={busy}
                onClick={onUndo}
              >
                <IconArrowBackUp />
                <span className="tw-px-0.5">Undo</span>
              </button>
            )}
            <div className="tw-flex tw-items-center">
              <RatingButton
                rating="down"
                active={row.rating === "down"}
                disabled={!canRate}
                onClick={() => onRate("down")}
              />
              <RatingButton
                rating="up"
                active={row.rating === "up"}
                disabled={!canRate}
                onClick={() => onRate("up")}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
